class MainCart extends HTMLElement {
  constructor() {
    super();
    this.onClick = this.onClick.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.onCartUpdated = this.onCartUpdated.bind(this);
    this.refreshController = null;

    // Cart mutations are serialised through this chain. cart/change.js is not safe to run
    // concurrently - two in-flight requests can resolve against different cart states and leave the
    // wrong quantity - so each update waits for the previous one. AbortController is deliberately not
    // used here: aborting a POST that already reached the server leaves the cart in an unknown state.
    this.queue = Promise.resolve();

    // Number of updates requested but not yet settled. CartAPI.notify() fires from inside the awaited
    // request, so this is still >= 1 when our own response arrives - a response is the final one exactly
    // when this equals 1, which is how superseded intermediate renders are skipped.
    this.queued = 0;
  }

  connectedCallback() {
    this.sections = ['main-cart', 'cart-drawer', 'cart-icon-bubble'];

    this.addEventListener('click', this.onClick);
    this.addEventListener('change', this.onChange);
    this.addEventListener('submit', this.onSubmit);

    if (window.CartAPI) {
      window.CartAPI.subscribe(this.onCartUpdated);
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('change', this.onChange);
    this.removeEventListener('submit', this.onSubmit);

    if (this.refreshController) this.refreshController.abort();

    if (window.CartAPI) {
      const index = window.CartAPI.listeners.indexOf(this.onCartUpdated);
      if (index > -1) window.CartAPI.listeners.splice(index, 1);
    }
  }

  onClick(event) {
    const stepperButton = event.target.closest('.cart-item__stepper-button');
    if (stepperButton) {
      event.preventDefault();
      this.onStepperClick(stepperButton);
      return;
    }

    const removeLink = event.target.closest('[data-cart-remove]');
    if (removeLink) {
      // The href is a real native remove URL and stays the no-JS fallback; only intercept when JS runs.
      event.preventDefault();
      this.requestUpdate(removeLink.dataset.line, 0);
    }
  }

  onChange(event) {
    const quantityInput = event.target.closest('.cart-item__stepper-input');
    if (quantityInput) {
      this.requestUpdate(quantityInput.dataset.line, quantityInput.value);
      return;
    }

    const note = event.target.closest('[name="note"]');
    if (note) {
      this.saveNote(note.value);
    }
  }

  onSubmit(event) {
    // The items form exists purely as the no-JS baseline. With JS active the change handler has
    // already committed the quantity, so let the native POST go only for the summary/checkout form.
    if (event.target.closest('#main-cart-form')) {
      event.preventDefault();
    }
  }

  onStepperClick(button) {
    const stepper = button.closest('.cart-item__stepper');
    const input = stepper ? stepper.querySelector('.cart-item__stepper-input') : null;
    if (!input) return;

    const current = parseInt(input.value, 10) || 0;
    const max = input.max === '' ? null : parseInt(input.max, 10);
    let next = button.name === 'plus' ? current + 1 : current - 1;

    if (next < 0) return;
    if (button.name === 'plus' && max !== null && next > max) return;

    // Update optimistically so rapid clicks accumulate from the latest value rather than a stale one.
    input.value = next;
    this.requestUpdate(input.dataset.line, next);
  }

  requestUpdate(line, quantity) {
    if (!window.CartAPI || line === undefined) return;

    this.clearError();
    this.setLineBusy(line, true);
    this.queued += 1;
    this.setAttribute('aria-busy', 'true');

    this.queue = this.queue.then(async () => {
      try {
        await window.CartAPI.changeLineItem(line, quantity, this.sections);
      } catch (error) {
        // A failed update never re-renders, so clear the busy state here or the row's controls
        // would stay permanently disabled.
        this.showError(error);
        this.clearBusyLines();
      } finally {
        this.queued -= 1;
        if (this.queued === 0) {
          this.removeAttribute('aria-busy');
        }
      }
    });

    return this.queue;
  }

  saveNote(value) {
    if (!window.CartAPI) return;

    // Queued on the same chain as quantity changes so a note can never be overtaken by a cart update
    // that would re-render the section from a cart the note had not reached yet.
    this.queue = this.queue.then(async () => {
      this.savingNote = true;
      try {
        await window.CartAPI.updateNote(value);
      } catch (error) {
        this.showError(error);
      } finally {
        this.savingNote = false;
      }
    });

    return this.queue;
  }

  setLineBusy(line, isBusy) {
    const item = this.querySelector(`.cart-item[data-line="${line}"]`);
    if (item) item.classList.toggle('is-loading', isBusy);
  }

  clearBusyLines() {
    this.querySelectorAll('.cart-item.is-loading').forEach((item) => {
      item.classList.remove('is-loading');
    });
  }

  onCartUpdated(state) {
    // Skip superseded responses: only the last outstanding update should paint.
    if (!state || this.queued > 1) return;

    // A note save requests no sections precisely because nothing needs repainting - the shopper's text
    // is already on screen and the server now agrees with it. Without this guard the branch below
    // treated it as a foreign update and refetched the section, which is what was overwriting the note.
    if (this.savingNote) return;

    // An update from elsewhere - the Cart Drawer is on every page and requests only its own sections -
    // leaves this page stale. Pull our section fresh rather than modifying the locked drawer.
    if (!state.sections || !state.sections['main-cart']) {
      this.refreshFromServer();
      return;
    }

    const parsed = new DOMParser().parseFromString(state.sections['main-cart'], 'text/html');
    const replacement = parsed.getElementById('main-cart-content');

    if (!replacement) {
      // Last item removed: the section renders the empty state instead, so a reload is the honest path.
      window.location.reload();
      return;
    }

    const focusTarget = this.describeFocus();
    this.innerHTML = replacement.innerHTML;
    this.restoreFocus(focusTarget);
  }

  // Idempotent GET, so unlike the cart mutations this one is safe (and correct) to abort on supersede.
  async refreshFromServer() {
    if (this.refreshController) this.refreshController.abort();
    this.refreshController = new AbortController();
    const signal = this.refreshController.signal;

    const separator = window.location.search ? '&' : '?';
    const url = `${window.location.pathname}${window.location.search}${separator}section_id=main-cart`;

    try {
      const response = await fetch(url, { signal });
      if (!response.ok) return;

      const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
      const replacement = parsed.getElementById('main-cart-content');

      if (!replacement) {
        window.location.reload();
        return;
      }

      const focusTarget = this.describeFocus();
      this.innerHTML = replacement.innerHTML;
      this.restoreFocus(focusTarget);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Failed to refresh cart', error);
      }
    }
  }

  describeFocus() {
    const active = document.activeElement;
    if (!active || !this.contains(active)) return null;

    // A note being typed right now has not been committed yet (change fires on blur), so carry the
    // text and caret across the render rather than letting the server value overwrite it.
    if (active.name === 'note') {
      return {
        note: true,
        value: active.value,
        selectionStart: active.selectionStart,
        selectionEnd: active.selectionEnd
      };
    }

    const line = active.dataset ? active.dataset.line : null;
    if (!line) return null;

    if (active.classList.contains('cart-item__stepper-input')) {
      return { line, selector: '.cart-item__stepper-input' };
    }
    if (active.classList.contains('cart-item__stepper-button')) {
      return { line, selector: `.cart-item__stepper-button[name="${active.name}"]` };
    }
    return null;
  }

  restoreFocus(target) {
    if (!target) return;

    if (target.note) {
      const note = this.querySelector('[name="note"]');
      if (!note) return;

      note.value = target.value;
      note.focus();
      if (target.selectionStart !== null && target.selectionStart !== undefined) {
        note.setSelectionRange(target.selectionStart, target.selectionEnd);
      }
      return;
    }

    const stepper = this.querySelector(`.cart-item__stepper[data-line="${target.line}"]`);
    const element = stepper ? stepper.querySelector(target.selector) : null;
    if (element) element.focus();
  }

  get errorRegion() {
    return this.querySelector('[data-cart-error]');
  }

  showError(error) {
    const region = this.errorRegion;
    if (!region) return;

    const strings = window.themeVariables?.strings || {};
    const message =
      (error && (error.description || error.message)) ||
      strings.cartUpdateError ||
      strings.cartError ||
      'There was an error updating your cart.';

    region.textContent = message;
    region.hidden = false;
  }

  clearError() {
    const region = this.errorRegion;
    if (!region) return;

    region.textContent = '';
    region.hidden = true;
  }
}

if (!customElements.get('main-cart')) {
  customElements.define('main-cart', MainCart);
}
